import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, CalendarOff, Send, Info, AlertTriangle, X, CheckCircle2 } from 'lucide-react'
import { apiGet, postJson } from '../../lib/api.js'
import { apiErrorText } from '../../lib/apiError.js'
import { LEAVE_PERSONAL, LEAVE_SICK, leaveTypeMeta } from '../../lib/timetable.js'
import { leaveQuota, yearRange } from '../../lib/leave.js'
import {
  assessLeave, statusLabel, statusTone, fmtRange, bkkToday, canCancel,
  applyWarning, skipReason,
} from '../../lib/leaveRequest.js'
import { decNotice, refreshNotice } from '../../lib/notice.js'
import AttachPicker from '../../components/AttachPicker.jsx'
import ConfirmDialog from '../../components/ConfirmDialog.jsx'

const FIELD = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-800 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'

/**
 * พื้นที่ของฉัน › ขอลา — the employee files ลากิจ / ลาป่วย and watches what happened
 * to it.
 *
 * Before this existed there was no channel at all: leave only became real when HR
 * typed a ลากิจ/ลาป่วย row into employee_timetable after the fact, so the request,
 * the reason and any medical certificate were never recorded anywhere.
 *
 * 🔑 Approving a request writes into those SAME employee_timetable rows (through
 * api 290 / api 354, in functions/api/hr/leave-request/[id].js) rather than into a
 * parallel store — so an approved leave is a real roster fact that ตารางเวร, the
 * clock-in report and payroll all already know how to read.
 *
 * The quota shown here is computed with the SAME `leaveQuota()` the HR การลา tab
 * uses. Employee.leavequota is an older, different answer, and showing the employee
 * one number while HR sees another is how a quota argument starts.
 */
export default function MeLeave() {
  const year = new Date().getFullYear()

  const [profile, setProfile] = useState(null)
  const [taken, setTaken] = useState({ personal: 0, sick: 0 })
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')

  const [type, setType] = useState(LEAVE_PERSONAL)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [reason, setReason] = useState('')
  const [files, setFiles] = useState([])
  const [saving, setSaving] = useState(false)
  const [formErr, setFormErr] = useState('')
  const [confirmSend, setConfirmSend] = useState(false)
  const [cancelling, setCancelling] = useState(null)
  const [flash, setFlash] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setErr('')
    try {
      const [p, leaveRows, mine] = await Promise.all([
        apiGet('/api/me/profile').catch(() => null),
        // type=leave flips Xano into its ลากิจ/ลาป่วย branch — a dozen rows for the
        // year instead of ~300 shifts each dragging three aggregate addons.
        apiGet('/api/me/clockin', { ...yearRange(year), type: 'leave', per_page: 100 }).catch(() => null),
        apiGet('/api/me/leave').catch(() => null),
      ])
      setProfile(p)
      const list = Array.isArray(leaveRows) ? leaveRows : (leaveRows?.items || [])
      let personal = 0
      let sick = 0
      for (const r of list) {
        if (r?.active === false) continue
        if (r?.type === LEAVE_SICK) sick += 1
        else if (r?.type === LEAVE_PERSONAL) personal += 1
      }
      setTaken({ personal, sick })
      setRows(Array.isArray(mine?.items) ? mine.items : [])
    } catch (e) {
      setErr(apiErrorText(e, 'โหลดข้อมูลไม่สำเร็จ'))
    } finally {
      setLoading(false)
    }
  }, [year])

  useEffect(() => { load() }, [load])

  // Opening this tab IS reading the decisions on it, so the dot clears here rather
  // than demanding a second click on every row.
  useEffect(() => {
    const unseen = rows.filter((r) => r.status !== 'pending' && r.status !== 'cancelled' && !r.decision_seen_at)
    if (!unseen.length) return
    let alive = true
    Promise.all(unseen.map((r) => postJson(`/api/me/leave/${r.id}`, { action: 'seen' }).catch(() => null)))
      .then(() => {
        if (!alive) return
        decNotice('leave_decided', unseen.length)
        setRows((prev) => prev.map((r) => (unseen.some((u) => u.id === r.id) ? { ...r, decision_seen_at: Date.now() } : r)))
      })
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length])

  const quota = useMemo(() => leaveQuota(profile, year, taken), [profile, year, taken])
  const assessment = useMemo(
    () => assessLeave({ type, date_from: from, date_to: to || from, fileCount: files.length }),
    [type, from, to, files.length],
  )

  function reset() {
    setType(LEAVE_PERSONAL); setFrom(''); setTo(''); setReason(''); setFiles([]); setFormErr('')
  }

  async function submit() {
    setConfirmSend(false)
    setSaving(true)
    setFormErr('')
    try {
      await postJson('/api/me/leave', {
        type,
        date_from: from,
        date_to: to || from,
        reason: reason.trim(),
        file: files,
      })
      reset()
      setFlash('ส่งคำขอลาเรียบร้อย — ฝ่ายบุคคลหรือผู้จัดการจะพิจารณาและแจ้งผลกลับ')
      await load()
      refreshNotice()
    } catch (e) {
      setFormErr(apiErrorText(e, 'ส่งคำขอไม่สำเร็จ'))
    } finally {
      setSaving(false)
    }
  }

  async function cancel(row) {
    setCancelling(null)
    try {
      await postJson(`/api/me/leave/${row.id}`, { action: 'cancel' })
      await load()
    } catch (e) {
      setErr(apiErrorText(e, 'ยกเลิกไม่สำเร็จ'))
    }
  }

  if (loading) {
    return <div className="py-16 text-center text-slate-400"><Loader2 size={20} className="animate-spin inline" /></div>
  }

  return (
    <div className="max-w-3xl space-y-5">
      {err && <p className="text-[13px] text-rose-600">{err}</p>}

      {/* Quota — shown before the form, because "how many do I have left" is the
          question people open this page with. */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-3">
          <CalendarOff size={15} className="text-slate-400" />
          <h2 className="text-[14px] font-semibold text-slate-800">โควตาวันลา ปี {year}</h2>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <QuotaTile label="ลากิจ" q={quota.personal} tone="amber" />
          <QuotaTile label="ลาป่วย" q={quota.sick} tone="rose" />
        </div>
        <p className="mt-2.5 text-[11.5px] text-slate-400">
          นับจากวันลาที่บันทึกในตารางเวรของปีนี้ · ลากิจคิดตามสัดส่วนหากเพิ่งผ่านทดลองงานระหว่างปี
        </p>
      </div>

      {/* The form */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
        <h2 className="text-[14px] font-semibold text-slate-800 mb-3.5">ขอลา</h2>

        <div className="flex gap-2 mb-3.5">
          {[LEAVE_PERSONAL, LEAVE_SICK].map((t) => {
            const meta = leaveTypeMeta(t)
            const on = type === t
            return (
              <button
                key={t}
                type="button"
                onClick={() => setType(t)}
                className={`px-3.5 py-2 rounded-xl text-[13px] font-medium ring-1 transition ${
                  on ? meta.tone : 'bg-white text-slate-500 ring-slate-200 hover:bg-slate-50'
                }`}
              >
                {meta.label}
              </button>
            )
          })}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="ตั้งแต่วันที่">
            <input
              type="date" value={from} min={bkkToday()}
              onChange={(e) => { setFrom(e.target.value); if (to && e.target.value > to) setTo(e.target.value) }}
              className={FIELD}
            />
          </Field>
          <Field label="ถึงวันที่" hint="เว้นว่างถ้าลาวันเดียว">
            <input
              type="date" value={to} min={from || bkkToday()}
              onChange={(e) => setTo(e.target.value)} className={FIELD}
            />
          </Field>
        </div>

        {assessment.days > 0 && (
          <p className="mt-2 text-[12.5px] text-slate-500">
            รวม <span className="font-semibold text-slate-700 tabular-nums">{assessment.days}</span> วัน
          </p>
        )}

        <div className="mt-3">
          <Field label="เหตุผล" hint="ไม่บังคับ แต่ช่วยให้พิจารณาได้เร็วขึ้น">
            <textarea
              rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
              className={`${FIELD} resize-y`} placeholder="เช่น พาแม่ไปโรงพยาบาล"
            />
          </Field>
        </div>

        <div className="mt-3">
          <AttachPicker
            files={files}
            onChange={setFiles}
            uploadUrl="/api/me/leave-file"
            hint="แนบใบรับรองแพทย์ หรือเอกสารประกอบ (รูปภาพ / PDF)"
            disabled={saving}
          />
        </div>

        {/* Advisory notes. NOT validation — ลากิจ over 3 days is submittable by the
            owner's explicit instruction; the employee may ask, HR meets them first,
            and there is no guarantee. Saying that plainly is better than a button
            that refuses and explains nothing. */}
        {assessment.notes.map((nt, i) => (
          <div key={i} className="mt-3 flex gap-2 rounded-xl bg-amber-50 ring-1 ring-amber-200 px-3 py-2.5">
            <AlertTriangle size={14} className="text-amber-600 shrink-0 mt-0.5" />
            <p className="text-[12.5px] text-amber-800 leading-relaxed">{nt.text}</p>
          </div>
        ))}

        {assessment.blocking && (
          <p className="mt-3 text-[12.5px] text-rose-600">{assessment.blocking}</p>
        )}
        {formErr && <p className="mt-3 text-[12.5px] text-rose-600">{formErr}</p>}
        {flash && (
          <div className="mt-3 flex gap-2 rounded-xl bg-emerald-50 ring-1 ring-emerald-200 px-3 py-2.5">
            <CheckCircle2 size={14} className="text-emerald-600 shrink-0 mt-0.5" />
            <p className="text-[12.5px] text-emerald-800">{flash}</p>
          </div>
        )}

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={() => setConfirmSend(true)}
            disabled={saving || !!assessment.blocking || !from}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[13.5px] font-semibold text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} ส่งคำขอ
          </button>
        </div>
      </div>

      {/* History */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
        <h2 className="text-[14px] font-semibold text-slate-800 mb-3">คำขอของฉัน</h2>
        {rows.length === 0 ? (
          <p className="text-[13px] text-slate-400 py-4 text-center">ยังไม่มีคำขอลา</p>
        ) : (
          <div className="space-y-2.5">
            {rows.map((r) => (
              <RequestRow key={r.id} r={r} onCancel={() => setCancelling(r)} />
            ))}
          </div>
        )}
      </div>

      {confirmSend && (
        <ConfirmDialog
          title="ส่งคำขอลา?"
          message={
            `${leaveTypeMeta(type).label} ${fmtRange(from, to || from)} รวม ${assessment.days} วัน` +
            (assessment.needsMeeting
              ? '\n\n⚠️ ลากิจติดต่อกันเกิน 3 วัน — ฝ่ายบุคคลจะขอนัดคุยก่อนพิจารณา และอาจไม่ได้รับอนุมัติ'
              : '') +
            (assessment.wantsCertificate && !assessment.hasCertificate
              ? '\n\n⚠️ ยังไม่ได้แนบใบรับรองแพทย์ — ส่งได้ แต่ฝ่ายบุคคลอาจขอเพิ่มภายหลัง'
              : '')
          }
          confirmLabel="ส่งคำขอ"
          tone={assessment.needsMeeting ? 'danger' : 'default'}
          onConfirm={submit}
          onCancel={() => setConfirmSend(false)}
        />
      )}

      {cancelling && (
        <ConfirmDialog
          title="ยกเลิกคำขอนี้?"
          message={`${leaveTypeMeta(cancelling.type).label} ${fmtRange(cancelling.date_from, cancelling.date_to)} — คำขอจะถูกถอนออกจากคิวของฝ่ายบุคคล`}
          confirmLabel="ยกเลิกคำขอ"
          tone="danger"
          onConfirm={() => cancel(cancelling)}
          onCancel={() => setCancelling(null)}
        />
      )}
    </div>
  )
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-[12px] font-medium text-slate-500 mb-1">
        {label} {hint && <span className="font-normal text-slate-400">· {hint}</span>}
      </span>
      {children}
    </label>
  )
}

// ⚠️ Static class strings only. Tailwind scans source text, so an interpolated
// `bg-${tone}-50` is never generated and the tile renders unstyled — the same class
// of trap docs/CONVENTIONS.md records for disabled inputs.
const QUOTA_TONE = {
  amber: 'bg-amber-50/60 ring-amber-200',
  rose: 'bg-rose-50/60 ring-rose-200',
}

function QuotaTile({ label, q, tone }) {
  const bad = q.over
  const low = !bad && q.remaining <= 2
  return (
    <div className={`rounded-xl px-3.5 py-3 ring-1 ${bad ? 'bg-rose-100 ring-rose-300' : (QUOTA_TONE[tone] || 'bg-slate-50 ring-slate-200')}`}>
      <div className="text-[12px] text-slate-500">{label}</div>
      <div className="mt-0.5 flex items-baseline gap-1.5">
        <span className={`text-[20px] font-semibold tabular-nums ${bad ? 'text-rose-600' : low ? 'text-amber-600' : 'text-slate-800'}`}>
          {Math.max(0, q.remaining)}
        </span>
        <span className="text-[12px] text-slate-400 tabular-nums">เหลือ / ใช้ไป {q.used} จาก {q.quota} วัน</span>
      </div>
      {bad && <div className="mt-1 text-[11.5px] text-rose-600">ใช้เกินโควตาแล้ว</div>}
    </div>
  )
}

function RequestRow({ r, onCancel }) {
  const meta = leaveTypeMeta(r.type)
  const warn = applyWarning(r)
  const skipped = Array.isArray(r.skipped) ? r.skipped : []
  return (
    <div className="rounded-xl border border-slate-200 px-3.5 py-3">
      <div className="flex items-start gap-2.5">
        <span className={`px-2 py-0.5 rounded-lg text-[11.5px] font-medium ring-1 shrink-0 ${meta.tone}`}>{meta.short}</span>
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] font-medium text-slate-800">
            {fmtRange(r.date_from, r.date_to)}
            <span className="ml-2 text-[12px] font-normal text-slate-400 tabular-nums">{r.days} วัน</span>
          </div>
          {r.reason && <div className="text-[12.5px] text-slate-500 mt-0.5 break-words">{r.reason}</div>}
          {Array.isArray(r.file) && r.file.length > 0 && (
            <div className="text-[11.5px] text-slate-400 mt-0.5">แนบไฟล์ {r.file.length} รายการ</div>
          )}
        </div>
        <span className={`px-2 py-0.5 rounded-lg text-[11.5px] font-medium ring-1 shrink-0 ${statusTone(r.status)}`}>
          {statusLabel(r.status)}
        </span>
      </div>

      {r.decision_note && (
        <div className="mt-2 flex gap-1.5 text-[12.5px] text-slate-600">
          <Info size={13} className="text-slate-400 shrink-0 mt-0.5" />
          <span className="break-words">{r.decision_note}</span>
        </div>
      )}

      {warn && (
        <p className="mt-2 text-[12px] text-amber-700 bg-amber-50 ring-1 ring-amber-200 rounded-lg px-2.5 py-1.5">{warn}</p>
      )}

      {skipped.length > 0 && (
        <ul className="mt-1.5 space-y-0.5">
          {skipped.map((s, i) => (
            <li key={i} className="text-[11.5px] text-slate-500">· {s.date} — {skipReason(s)}</li>
          ))}
        </ul>
      )}

      {canCancel(r) && (
        <div className="mt-2 flex justify-end">
          <button
            type="button" onClick={onCancel}
            className="inline-flex items-center gap-1 text-[12px] text-slate-400 hover:text-rose-600"
          >
            <X size={12} /> ยกเลิกคำขอ
          </button>
        </div>
      )}
    </div>
  )
}
